import type { IPersonalData } from 'AppRoutes/context/SubscriptionContext';
import type { CartItem } from 'utils/types/Cart';
import type { OrderItem } from 'utils/types/Order';

export interface BeerStoreOrderData {
  shipping?: {
    id: string;
    price: number;
    description?: string;
    deliveryTime?: string;
  };
}

export const getCartItemsForPayment = (
  items: CartItem[],
  hasActiveSubscription: boolean,
) =>
  items.map(item => {
    const unitPrice = hasActiveSubscription
      ? item.beer.subscriberPrice
      : item.beer.price;
    return {
      beerId: item.beer.id,
      beerName: item.beer.name,
      quantity: item.quantity,
      unit_price: unitPrice,
      totalPrice: Math.round(unitPrice * item.quantity * 100) / 100,
      isSubscriberPrice: hasActiveSubscription,
    };
  });

export const buildOrderItems = (
  items: CartItem[],
  hasActiveSubscription: boolean,
): OrderItem[] =>
  items.map(item => {
    const unitPrice = hasActiveSubscription
      ? item.beer.subscriberPrice
      : item.beer.price;
    return {
      beerId: item.beer.id,
      beerName: item.beer.name,
      brewery: item.beer.brewery,
      quantity: item.quantity,
      unitPrice,
      totalPrice: Math.round(unitPrice * item.quantity * 100) / 100,
      isSubscriberPrice: hasActiveSubscription,
      originalUnitPrice:
        hasActiveSubscription && item.beer.price !== item.beer.subscriberPrice
          ? item.beer.price
          : undefined,
    };
  });

export const validateBeerStoreOrderPrerequisites = (
  orderData: BeerStoreOrderData | null,
  personalData: IPersonalData,
): void => {
  if (!orderData?.shipping) {
    throw new Error(
      'Dados de shipping não encontrados. Por favor, volte e selecione uma opção de frete.',
    );
  }

  const isDelivery = orderData.shipping.id !== 'retira';
  if (
    isDelivery &&
    (!personalData?.address || !personalData.address.street)
  ) {
    throw new Error(
      'Dados de endereço não encontrados. Por favor, complete seu perfil antes de continuar.',
    );
  }
};

export const buildShippingData = (
  orderData: BeerStoreOrderData,
  personalData: IPersonalData,
) => {
  if (orderData.shipping?.id === 'retira') {
    return {
      method: 'pickup',
      address: {
        street: 'Clube da 42 - Cervejaria Artesanal',
        city: 'Araraquara',
        state: 'SP',
        zipCode: '14802-140',
        country: 'BR',
      },
      estimatedDelivery:
        orderData.shipping?.description || 'De quarta à sábado - 18h às 23h',
    };
  }

  return {
    method: 'delivery',
    address: {
      street: `${personalData?.address?.street || 'Rua não informada'}, ${personalData?.address?.number || personalData?.number || 'S/N'}`,
      neighborhood:
        personalData?.address?.neighborhood || personalData?.neighborhood || '',
      city:
        personalData?.address?.city ||
        personalData?.city ||
        'Cidade não informada',
      state: personalData?.address?.uf || personalData?.uf || 'SP',
      zipCode:
        personalData?.address?.zip || personalData?.cep || '00000-000',
      complement:
        personalData?.address?.complement || personalData?.complement || '',
      country: 'BR',
    },
    estimatedDelivery:
      orderData.shipping?.deliveryTime ||
      orderData.shipping?.description ||
      'Prazo não informado',
  };
};

interface BuildOrderPayloadParams {
  userId: string;
  userEmail: string;
  items: OrderItem[];
  subtotal: number;
  shippingCost: number;
  total: number;
  hasActiveSubscription: boolean;
  orderData: BeerStoreOrderData;
  personalData: IPersonalData;
  payment: {
    method: 'credit_card' | 'pix';
    status: 'approved' | 'pending';
    transactionId: string | number;
    paymentId?: string | number;
    amount: number;
    installments?: number;
    lastFourDigits?: string;
    cardBrand?: string;
  };
  orderStatus: 'confirmed' | 'pending';
}

export const buildBeerStoreOrderPayload = ({
  userId,
  userEmail,
  items,
  subtotal,
  shippingCost,
  total,
  hasActiveSubscription,
  orderData,
  personalData,
  payment,
  orderStatus,
}: BuildOrderPayloadParams) => ({
  userId,
  userEmail,
  items,
  subtotal,
  shippingCost,
  total,
  isSubscriber: hasActiveSubscription,
  shipping: buildShippingData(orderData, personalData),
  payment: {
    ...payment,
    currency: 'BRL',
    processedAt: new Date().toISOString(),
    ...(payment.status === 'approved'
      ? { approvedAt: new Date().toISOString() }
      : {}),
  },
  status: orderStatus,
});

export const sanitizeCpf = (cpf: string): string => cpf.replace(/\D/g, '');
